import React from 'react';
import PropTypes from 'prop-types';
import every from 'lodash/every';
import styled from 'styled-components';

import { AppText } from 'domain/appText';

import BirthdayInput from './Input';

const StyledGuestBirthdayInput = styled.div`
    position: relative;
    padding: 0 20px;
    margin: 0;
    font-size: 0;

    &.required {
       border: 2px solid #fff; -moz-box-shadow: inset 0 0 0px 2px #ffebb2; -webkit-box-shadow: inset 0 0 0px 2px #ffebb2; box-shadow: inset 0 0 0px 2px #ffebb2;
    }
    div.column{
      width: 36px;
      display:inline-block;
      vertical-align: top;
      font-size: 14px;
      position: relative;

      &.day, &.month{
        width: 28px;
      }

      label{
        margin: 0;
        color: #86a3bf;
        opacity: 0.75;
        font-weight: normal;
        position: absolute;
        top: 5px;
        font-size: 10px;
        line-height: 12px;
        left: 0px;
      }

      input{
        border: 0;
        padding: 25px 0px 5px 0px;
        font-size: 14px;
        height: 17px;
        line-height: 17px;
        width: 100%;
        outline: none;
        background-color: transparent;
        &:-moz-read-only { /* For Firefox */
          cursor: not-allowed;
        }
        &:read-only {
          cursor: not-allowed;
        }
      }
    }
    .divider{
      color: #d3e4eb;
      font-size: 21px;
      line-height: 24px;
      width: 13px;
      text-align: center;
      display:inline-block;
      vertical-align: bottom;
    }
`;

export const parseValue = (value, dateFormat, divider) => {
  const valueArray = value.split(divider);
  const formatArray = dateFormat.split(divider);
  if (valueArray.length !== formatArray.length) {
    return {};
  }
  const keyMap = {
    year: 'YYYY',
    month: 'MM',
    day: 'DD',
  };

  return Object.keys(keyMap).reduce((agg, key) => {
    const index = formatArray.findIndex(f => f === keyMap[key]);
    return {
      ...agg,
      [key]: valueArray[index],
    };
  }, {});
};

const getValueAsIsoString = ({ year, month, day }) => `${year}-${month}-${day}`;

export const buildValue = ({ year, month, day }, dateFormat, divider) =>
  dateFormat.split(divider).map((key) => {
    switch (key) {
      case 'YYYY':
      case 'YY':
        return year;
      case 'MM': {
        return month;
      }
      case 'DD': {
        return day;
      }
      default:
    }
    return '';
  }).join(divider);

const getDivider = dateFormat => dateFormat.replace(/\w/g, '').charAt(0);

class GuestBirthdayInput extends React.Component {
  static propTypes = {
    guestId: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    value: PropTypes.string,
    placeholder: PropTypes.string, // YYYY-MM-DD || MM/DD/YYYY || DD/MM/YY || MM.DD.YYYY
    dateFormat: PropTypes.string, // YYYY-MM-DD || MM/DD/YYYY || DD/MM/YY || MM.DD.YYYY
    htmlId: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onMove: PropTypes.func,
    className: PropTypes.string,
    validate: PropTypes.func,
    mapValue: PropTypes.shape({
      in: PropTypes.func, // from store to state
      out: PropTypes.func, // from state to store
    }),
    title: PropTypes.func,
    shouldFocus: PropTypes.bool,
    readonly: PropTypes.bool,
    nonRequired: PropTypes.bool,
    passportField: PropTypes.bool,
    canBeNull: PropTypes.bool,
    setRequiredField: PropTypes.func,
    unsetRequiredField: PropTypes.func,
  };
  static defaultProps = {
    value: '',
    onChange: () => {},
    onBlur: () => {},
    onMove: () => {},
    title: () => {},
    mapValue: {
      in: v => v,
      out: v => v,
    },
    validate: () => true,
    nonRequired: false,
    passportField: false,
    canBeNull: false,
    dateFormat: 'YYYY-MM-DD',
  };

  constructor(props) {
    super(props);
    const value = parseValue(props.mapValue.in(props.value) || '', props.dateFormat, getDivider(this.props.dateFormat));
    this.state = {
      year: '',
      month: '',
      day: '',
      ...value,
      previousValue: getValueAsIsoString(value),
      hasFocus: false,
    };
  }

  componentWillReceiveProps({ value: nextValue }) {
    const mappedValue = this.props.mapValue.in(nextValue);
    if (mappedValue !== this.state.value) {
      this.setState({
        value: mappedValue,
        valid: true,
        ...parseValue(mappedValue, this.props.dateFormat, getDivider(this.props.dateFormat)),
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      (nextState.year !== this.state.year) ||
      (nextState.month !== this.state.month) ||
      (nextState.day !== this.state.day) ||
      (nextState.valid !== this.state.valid) ||
      !!nextProps.shouldFocus ||
      this.props.dateFormat !== nextProps.dateFormat
    );
  }

  componentDidUpdate = () => {
    if (this.props.shouldFocus && this.state.hasFocus === false) {
      this.el.focus();
      this.el.select();
    }
  }

  onFocus = () => {
    clearTimeout(this.timeout);
    this.setState({ hasFocus: true });
    this.props.onFocus();
  }

  onBlur = () => {
    const { onChange, onBlur, validate, unsetRequiredField, setRequiredField, canBeNull, nonRequired, passportField } = this.props;
    this.setState({ hasFocus: false });
    onBlur();
    this.timeout = setTimeout(() => {
      const hasValues = every(['year', 'month', 'day'].map(key => this.state[key]));
      if (!hasValues && !canBeNull) {
        this.setState({ valid: false });
        setRequiredField();
        return;
      }

      const allEmpty = every(['year', 'month', 'day'].map(key => this.state[key] === ''));
      if (allEmpty && canBeNull) {
        if (!nonRequired && passportField) {
          this.props.setRequiredField();
        } else {
          unsetRequiredField();
        }

        this.setState({});
        onChange('');
        return;
      }

      const currentValue = getValueAsIsoString(this.state);
      const valid = validate(currentValue);
      this.setState({ valid });
      if (currentValue !== this.state.previousValue) {
        if (valid) {
          // value has changed, trigger onChange
          this.setState({ previousValue: currentValue });
          unsetRequiredField();
          onChange(currentValue);
        } else {
          setRequiredField();
        }
      }
    }, 17);
  }

  onChangeYear = (value) => {
    this.setState({ year: value });
  }

  onChangeMonth = (value) => {
    this.setState({ month: value });
  }

  onChangeDay = (value) => {
    this.setState({ day: value });
  }

  previousValue = '';

  ref = (el) => {
    this.el = el;
  }

  inputFactory = ({ appText, className, idPrefix, onChange }) => {
    const { value, valid } = this.state;
    const { title, guestId, readonly } = this.props;

    return (placeholder, val) => (
      <BirthdayInput
        appText={appText}
        className={className}
        id={`${idPrefix}-${guestId}`}
        placeholder={placeholder}
        value={val}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={onChange}
        readOnly={readonly}
        valid={valid}
        title={title(value)}
      />
    );
  }

  renderYear = (...args) => this.inputFactory({
    appText: AppText.Year,
    className: 'year',
    idPrefix: 'year',
    onChange: this.onChangeYear,
  })(...args);

  renderMonth = (...args) => this.inputFactory({
    appText: AppText.Month,
    className: 'month',
    idPrefix: 'month',
    onChange: this.onChangeMonth,
  })(...args);

  renderDay = (...args) => this.inputFactory({
    appText: AppText.Day,
    className: 'day',
    idPrefix: 'day',
    onChange: this.onChangeDay,
  })(...args);

  renderField(index) {
    const { dateFormat } = this.props;
    const placeholder = dateFormat.split(getDivider(this.props.dateFormat))[index];
    switch (placeholder) {
      case 'YYYY':
      case 'YY':
        return this.renderYear(placeholder, this.state.year);
      case 'MM':
        return this.renderMonth(placeholder, this.state.month);
      case 'DD':
        return this.renderDay(placeholder, this.state.day);
      default:
        return null;
    }
  }

  render() {
    return (
      <StyledGuestBirthdayInput>
        {this.renderField(0)}
        <div className="divider">/</div>
        {this.renderField(1)}
        <div className="divider">/</div>
        {this.renderField(2)}
      </StyledGuestBirthdayInput>
    );
  }
}


export default GuestBirthdayInput;



// WEBPACK FOOTER //
// ./src/components/molecules/GuestBirthdayInput/index.js